import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Typography,
  Button,
  Row,
  Col,
  Avatar,
  Tag,
  Space,
  Divider,
  Statistic,
  Spin,
  Alert,
  Timeline,
  Badge,
  Tooltip
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  DollarOutlined,
  TeamOutlined,
  ClockCircleOutlined as ClockIcon
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { getPaymentLedgerById } from "../Services/PaymentLedgerService";
import { PaymentStatusEnum, PaymentStatusLabels, PaymentStatusColors } from "Common/Constants/Enums/PaymentStatusEnum";
import {
  EducationLevelLabel
} from "Common/Constants/Enums/Individual/EducationLevelEnum";
import { CareerLevelLabel } from "Common/Constants/Enums/Individual/CareerLevelEnum";
import { ProfessionTypeLabel } from "Common/Constants/Enums/Individual/ProfessionTypeEnum";
import { getLocationString } from "Common/Components/LocationHelper";

// Payment source type enum
export const PaymentSourceTypeEnum = Object.freeze({
  CoursePurchase: 0,
  ExpertService: 1,
  Certificate: 2,
  Other: 99
});

// Payment source type labels
export const PaymentSourceTypeLabels = {
  [PaymentSourceTypeEnum.CoursePurchase]: "Course Purchase",
  [PaymentSourceTypeEnum.ExpertService]: "Expert Service",
  [PaymentSourceTypeEnum.Certificate]: "Certificate",
  [PaymentSourceTypeEnum.Other]: "Other"
};

// Payment source type colors
export const PaymentSourceTypeColors = {
  [PaymentSourceTypeEnum.CoursePurchase]: "gold",
  [PaymentSourceTypeEnum.ExpertService]: "purple",
  [PaymentSourceTypeEnum.Certificate]: "green",
  [PaymentSourceTypeEnum.Other]: "gray"
};

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;

const PaymentLedgerDetailPage = () => {
  const { ledgerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentDetails();
  }, [ledgerId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const data = await getPaymentLedgerById(ledgerId);
      setPaymentData(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching payment details:", err);
      setError("Failed to load payment details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD MMM YYYY, HH:mm");
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    return dayjs(dateString).fromNow();
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case PaymentStatusEnum.Pending:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case PaymentStatusEnum.Paid:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case PaymentStatusEnum.Canceled:
      case PaymentStatusEnum.Failed:
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  // Helper to convert string to number for enum lookup
  const parseEnumValue = (value) => {
    if (value === null || value === undefined) return undefined;
    return typeof value === 'string' ? parseInt(value, 10) : value;
  };

  // Get education level label - handle both string and number
  const getEducationLevelLabel = (level) => {
    const parsedLevel = parseEnumValue(level);
    const label = EducationLevelLabel[parsedLevel];
    return label || `Education Level ${level}`;
  };

  // Get career level label
  const getCareerLevelLabel = (level) => {
    const parsedLevel = parseEnumValue(level);
    const label = CareerLevelLabel[parsedLevel];
    return label || `Career Level ${level}`;
  };

  // Get profession label
  const getProfessionLabel = (profession) => {
    const parsedProfession = parseEnumValue(profession);
    const label = ProfessionTypeLabel[parsedProfession];
    return label || `Profession ${profession}`;
  };

  // Get status color for amount
  const getAmountColor = (status) => {
    switch (status) {
      case PaymentStatusEnum.Paid:
        return '#52c41a'; // Green
      case PaymentStatusEnum.Failed:
      case PaymentStatusEnum.Canceled:
        return '#ff4d4f'; // Red
      case PaymentStatusEnum.Pending:
        return '#faad14'; // Yellow
      default:
        return '#9A6B0E'; // Blue
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large">
          <span style={{ marginLeft: 12 }}>Loading payment details...</span>
        </Spin>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={fetchPaymentDetails}>
              Retry
            </Button>
          }
        />
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginTop: 16 }}
        >
          Back to List
        </Button>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="No Data"
          description="Payment details not found."
          type="warning"
          showIcon
        />
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginTop: 16 }}
        >
          Back to List
        </Button>
      </div>
    );
  }

  // Determine if it's a course purchase or expert service purchase
  const isCoursePurchase = paymentData.sourceType === PaymentSourceTypeEnum.CoursePurchase;
  const isExpertServicePurchase = paymentData.sourceType === PaymentSourceTypeEnum.ExpertService;
  const isCertificatePurchase = paymentData.sourceType === PaymentSourceTypeEnum.Certificate;
  const isOtherPurchase = paymentData.sourceType === PaymentSourceTypeEnum.Other;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginBottom: 16 }}
        >
          Back to Payment Ledger
        </Button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Payment Details
            </Title>
            <Text type="secondary">
              Transaction ID: #{paymentData.id}
            </Text>
          </div>

          <div>
            <Space>
              <Tag color="gold" style={{ fontSize: '14px', padding: '4px 12px' }}>
                <CreditCardOutlined style={{ marginRight: 4 }} />
                {paymentData.paymentProvider}
              </Tag>
              <Badge
                color={PaymentStatusColors[paymentData.status]}
                text={
                  <Space>
                    {getStatusIcon(paymentData.status)}
                    <Text strong>{PaymentStatusLabels[paymentData.status]}</Text>
                  </Space>
                }
              />
            </Space>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Transaction Details */}
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            {/* Transaction Summary Card */}
            <Col span={24}>
              <Card title="Transaction Summary" variant="borderless">
                <Row gutter={[32, 16]}>
                  <Col xs={24} sm={12} md={12}>
                    <Statistic
                      title="Amount"
                      value={paymentData.amount}
                      precision={2}
                      prefix="$"
                      valueStyle={{
                        color: getAmountColor(paymentData.status),
                        fontSize: '32px',
                        fontWeight: 'bold'
                      }}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={12}>
                    <Statistic
                      title="Payment Source"
                      value={PaymentSourceTypeLabels[paymentData.sourceType]}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Learner Information */}
            <Col span={24}>
              <Card
                title={
                  <Space>
                    <UserOutlined />
                    <span>Learner Information</span>
                  </Space>
                }
                variant="borderless"
              >
                <Row gutter={[32, 16]}>
                  <Col xs={24} md={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Avatar
                        size={100}
                        src={paymentData.learner?.profileImage}
                        icon={<UserOutlined />}
                      />
                      <div style={{ marginTop: 12 }}>
                        <Tooltip title="Learner Code">
                          <Tag color="gold" style={{ cursor: 'pointer' }}>
                            <IdcardOutlined style={{ marginRight: 4 }} />
                            {paymentData.learner?.learnerCode || "N/A"}
                          </Tag>
                        </Tooltip>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={16}>
                    <Descriptions column={1} size="middle">
                      <Descriptions.Item label="Full Name">
                        <Text strong style={{ fontSize: '18px' }}>
                          {paymentData.learner?.firstName} {paymentData.learner?.lastName}
                        </Text>
                      </Descriptions.Item>

                      <Descriptions.Item label="Email">
                        <Space>
                          <MailOutlined />
                          <Text copyable>{paymentData.learner?.email || "N/A"}</Text>
                        </Space>
                      </Descriptions.Item>

                      <Descriptions.Item label="Phone">
                        <Space>
                          <PhoneOutlined />
                          <Text>{paymentData.learner?.phoneNo || "N/A"}</Text>
                        </Space>
                      </Descriptions.Item>

                      <Descriptions.Item label="Location">
                        <Space>
                          <EnvironmentOutlined />
                          <Text>
                            {getLocationString(paymentData?.learner)}
                          </Text>
                        </Space>
                      </Descriptions.Item>

                      <Descriptions.Item label="Education Level">
                        <Text>
                          {getEducationLevelLabel(paymentData.learner?.educationLevel)}
                        </Text>
                      </Descriptions.Item>

                      <Descriptions.Item label="Career Level">
                        <Text>
                          {paymentData.learner?.careerLevelText}
                        </Text>
                      </Descriptions.Item>

                      <Descriptions.Item label="Profession">
                        <Text>
                          {getProfessionLabel(paymentData.learner?.profession)}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Course Information or Expert Service Information */}
            {isCoursePurchase && paymentData.course && (
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <BookOutlined />
                      <span>Course Information</span>
                    </Space>
                  }
                  variant="borderless"
                >
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>Course ID</Text>
                      <Space>
                        <Tag>#{paymentData.course?.id}</Tag>
                        <Title level={5} style={{ margin: 0 }}>
                          {paymentData.course?.title}
                        </Title>
                      </Space>
                    </div>

                    <Descriptions column={1} size="middle">
                      <Descriptions.Item label="Description">
                        <Paragraph
                          ellipsis={{
                            rows: 3,
                            expandable: true,
                            symbol: 'more'
                          }}
                          style={{ marginBottom: 0 }}
                        >
                          {paymentData.course?.description || "No description available"}
                        </Paragraph>
                      </Descriptions.Item>

                      <Descriptions.Item label="Course Type">
                        <Tag color={paymentData.course?.isPaid ? "red" : "green"}>
                          {paymentData.course?.isPaid ? "Paid Course" : "Free Course"}
                        </Tag>
                      </Descriptions.Item>

                      <Descriptions.Item label="Estimated Time">
                        <Tag>{paymentData.course?.estimatedTime || "N/A"}</Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                </Card>
              </Col>
            )}

            {isExpertServicePurchase && paymentData.expertServicePurchase && (
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <TeamOutlined />
                      <span>Expert Service Information</span>
                    </Space>
                  }
                  variant="borderless"
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>Service ID</Text>
                          <Space>
                            <Tag>#{paymentData.expertServicePurchase?.expertService?.id}</Tag>
                            <Title level={5} style={{ margin: 0 }}>
                              {paymentData.expertServicePurchase?.expertService?.title}
                            </Title>
                          </Space>
                        </div>

                        <Descriptions column={1} size="middle">
                          <Descriptions.Item label="Description">
                            <Paragraph
                              ellipsis={{
                                rows: 3,
                                expandable: true,
                                symbol: 'more'
                              }}
                              style={{ marginBottom: 0 }}
                            >
                              {paymentData.expertServicePurchase?.expertService?.description || "No description available"}
                            </Paragraph>
                          </Descriptions.Item>
                        </Descriptions>
                      </Space>
                    </Col>

                    <Col xs={24} md={12}>
                      <Descriptions column={1} size="middle">
                        <Descriptions.Item label="Expert">
                          <Space>
                            <Avatar 
                              size="small" 
                              src={paymentData.expertServicePurchase?.expertService?.expert?.profileImage} 
                              icon={<UserOutlined />}
                            />
                            <Text>
                              {paymentData.expertServicePurchase?.expertService?.expert?.firstName} {paymentData.expertServicePurchase?.expertService?.expert?.lastName}
                            </Text>
                            <Text type="secondary">({paymentData.expertServicePurchase?.expertService?.expert?.expertCode})</Text>
                          </Space>
                        </Descriptions.Item>

                        <Descriptions.Item label="Units Purchased">
                          <Tag color="gold">{paymentData.expertServicePurchase?.unitsPurchased}</Tag>
                        </Descriptions.Item>

                        <Descriptions.Item label="Units Consumed">
                          <Tag color={paymentData.expertServicePurchase?.unitsConsumed > 0 ? "green" : "default"}>
                            {paymentData.expertServicePurchase?.unitsConsumed}
                          </Tag>
                        </Descriptions.Item>

                        <Descriptions.Item label="Unit Duration">
                          <Space>
                            <ClockIcon />
                            <Text>{paymentData.expertServicePurchase?.unitDurationMinutes} minutes</Text>
                          </Space>
                        </Descriptions.Item>

                        <Descriptions.Item label="Total Minutes">
                          <Text>{paymentData.expertServicePurchase?.totalMinutes} minutes</Text>
                        </Descriptions.Item>

                        <Descriptions.Item label="Consumed Minutes">
                          <Text>{paymentData.expertServicePurchase?.consumedMinutes} minutes</Text>
                        </Descriptions.Item>

                        <Descriptions.Item label="Unit Price">
                          <Text strong>{formatCurrency(
                            paymentData.expertServicePurchase?.expertService?.pricing?.unitPrice,
                            paymentData.expertServicePurchase?.expertService?.pricing?.currency
                          )}</Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Card>
              </Col>
            )}

            {isCertificatePurchase && (
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <BookOutlined />
                      <span>Certificate Information</span>
                    </Space>
                  }
                  variant="borderless"
                >
                  <Alert
                    message="Certificate Purchase"
                    description="This payment was for a certificate purchase."
                    type="info"
                    showIcon
                  />
                </Card>
              </Col>
            )}

            {isOtherPurchase && (
              <Col span={24}>
                <Card
                  title={
                    <Space>
                      <BookOutlined />
                      <span>Other Purchase Information</span>
                    </Space>
                  }
                  variant="borderless"
                >
                  <Alert
                    message="Other Purchase"
                    description="This payment was for other services or products."
                    type="info"
                    showIcon
                  />
                </Card>
              </Col>
            )}
          </Row>
        </Col>

        {/* Right Column - Timeline & Metadata */}
        <Col xs={24} lg={8}>
          <Row gutter={[16, 16]}>
            {/* Transaction Timeline */}
            <Col span={24}>
              <Card title="Transaction Timeline" variant="borderless">
                <Timeline>
                  <Timeline.Item
                    color="green"
                    dot={<CheckCircleOutlined style={{ fontSize: '16px' }} />}
                  >
                    <Space direction="vertical" size={2}>
                      <Text strong>Payment Processed</Text>
                      <Text type="secondary">{formatDate(paymentData.paidAt)}</Text>
                      <Text type="secondary">({formatRelativeTime(paymentData.paidAt)})</Text>
                    </Space>
                  </Timeline.Item>

                  <Timeline.Item
                    color="gold"
                    dot={<CalendarOutlined style={{ fontSize: '16px' }} />}
                  >
                    <Space direction="vertical" size={2}>
                      <Text strong>Transaction Created</Text>
                      <Text type="secondary">{formatDate(paymentData.createdOn)}</Text>
                      <Text type="secondary">({formatRelativeTime(paymentData.createdOn)})</Text>
                    </Space>
                  </Timeline.Item>

                  <Timeline.Item
                    color="gray"
                    dot={<CalendarOutlined style={{ fontSize: '16px' }} />}
                  >
                    <Space direction="vertical" size={2}>
                      <Text strong>Last Updated</Text>
                      <Text type="secondary">{formatDate(paymentData.lastModifiedOn)}</Text>
                      <Text type="secondary">({formatRelativeTime(paymentData.lastModifiedOn)})</Text>
                    </Space>
                  </Timeline.Item>
                </Timeline>
              </Card>
            </Col>

            {/* Payment Metadata */}
            <Col span={24}>
              <Card title="Payment Details" variant="borderless">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Transaction ID">
                    <Text copyable strong>
                      #{paymentData.id}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Learner ID">
                    <Text copyable>
                      {paymentData.learnerId}
                    </Text>
                  </Descriptions.Item>

                  {isCoursePurchase && (
                    <Descriptions.Item label="Course ID">
                      <Text copyable>
                        #{paymentData.course?.id}
                      </Text>
                    </Descriptions.Item>
                  )}

                  {isExpertServicePurchase && (
                    <Descriptions.Item label="Expert Service ID">
                      <Text copyable>
                        #{paymentData.expertServicePurchase?.expertService?.id}
                      </Text>
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item label="Payment Provider">
                    <Tag color="gold">{paymentData.paymentProvider}</Tag>
                  </Descriptions.Item>

                  <Descriptions.Item label="Payment Source">
                    <Tag color={PaymentSourceTypeColors[paymentData.sourceType]}>
                      {PaymentSourceTypeLabels[paymentData.sourceType]}
                    </Tag>
                  </Descriptions.Item>

                  <Descriptions.Item label="Status">
                    <Space>
                      {getStatusIcon(paymentData.status)}
                      <Tag color={PaymentStatusColors[paymentData.status]}>
                        {PaymentStatusLabels[paymentData.status]}
                      </Tag>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Amount">
                    <Space>
                      <DollarOutlined />
                      <Text strong style={{ color: getAmountColor(paymentData.status) }}>
                        {formatCurrency(paymentData.amount, paymentData.currency)}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Paid At">
                    {formatDate(paymentData.paidAt)}
                  </Descriptions.Item>

                  <Descriptions.Item label="Created On">
                    {formatDate(paymentData.createdOn)}
                  </Descriptions.Item>

                  <Descriptions.Item label="Last Modified">
                    {formatDate(paymentData.lastModifiedOn)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Footer */}
      <Divider />
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <Text type="secondary">
          Transaction ID: #{paymentData.id} •
          Last updated: {formatRelativeTime(paymentData.lastModifiedOn)}
        </Text>
      </div>
    </div>
  );
};

export default PaymentLedgerDetailPage;